import { Suspense, useCallback, useEffect } from "react";
import { Navigate, useLocation } from "react-router";
import { useUser } from "./components/menu/auth/is_loggedin";
import { useInitializeApp } from "./hooks/useInitializeApp";
import { useThemeEnforcement } from "./hooks/useThemeEnforcement";
import { useSyncAuthUser } from "./hooks/useSyncAuthUser";
import Loading from "./components/menu/loading/loading";
import { useAppSelector } from "./redux/store";
import { selectPlayerDetails } from "./redux/selectors";
import CreateGame from "./components/menu/create-game/create-game";
import Main from "./components/menu/main/main";
import Name from "./components/menu/name/name";
import SelectGame from "./components/menu/select-game/select-game";
import EnterGameCode from "./components/menu/enter-game/enter-game-code";
import GameManager from "./components/game/game";
import GameLayout from "./game-layout";
import { useSmartNavigate } from "./hooks/useSmartNavigate";
import Help from "./components/menu/help/help";
import Terms from "./components/menu/terms/terms";
import Privacy from "./components/menu/privacy/privacy";
import CodeOfConduct from "./components/menu/code-of-conduct/code-of-conduct";
import Contact from "./components/menu/contact/contact";
import BugReport from "./components/menu/bug-report/bug-report";
import Blog from "./components/menu/blog/blog";
import SupportUs from "./components/menu/supportus/supportus";
import Supporter from "./components/menu/supporter/supporter";
import Thankyou from "./components/menu/thankyou/thankyou";
import Zines from "./components/menu/zines/zines";
import Admin from "./components/menu/admin/admin";

const useGlobalBackHandler = () => {
  const navigate = useSmartNavigate();
  const location = useLocation();

  const handleBackspace = useCallback(
    (event: KeyboardEvent) => {
      if (event.target instanceof Element) {
        if (
          event.target.nodeName === "INPUT" ||
          event.target.nodeName === "TEXTAREA"
        ) {
          return;
        }
      }

      if (
        event.code === "Backspace" &&
        !location.pathname.startsWith("/game")
      ) {
        // If there's no previous page in the session's history stack for this app,
        // navigate to the main page. Otherwise, go back one step.
        if (window.history.state?.idx === 0 || window.history.state === null) {
          navigate("/");
        } else {
          navigate(-1);
        }
      }
    },
    [navigate, location.pathname]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleBackspace);
    return () => {
      window.removeEventListener("keydown", handleBackspace);
    };
  }, [handleBackspace]);
};

// Root layout component that wraps all routes
const RootLayout = () => {
  useInitializeApp();
  useThemeEnforcement();
  useSyncAuthUser();
  useGlobalBackHandler();

  return (
    <Suspense fallback={<Loading />}>
      <GameLayout />
    </Suspense>
  );
};

// Protected route guards as components
const RequirePlayerDetails = ({ children }: { children: React.ReactNode }) => {
  const playerDetails = useAppSelector(selectPlayerDetails);
  return playerDetails ? <>{children}</> : <Navigate to="/user" />;
};

const GameRoute = () => {
  const playerId = useAppSelector((state) => state.menu.playerId);
  return playerId ? (
    <GameManager playerId={playerId} />
  ) : (
    <Navigate to="/play" />
  );
};

const RequirePremium = ({ children }: { children: React.ReactNode }) => {
  const user = useUser();
  return user?.premium ? <>{children}</> : <Navigate to="/" />;
};

// Export route configuration for createBrowserRouter
export const routes = [
  {
    element: <RootLayout />,
    children: [
      { path: "/", element: <Main /> },
      { path: "/user", element: <Name /> },
      { path: "/user/setup", element: <Name setupMode={true} /> },
      {
        path: "/play",
        element: (
          <RequirePlayerDetails>
            <SelectGame />
          </RequirePlayerDetails>
        ),
      },
      {
        path: "/play/create",
        element: (
          <RequirePlayerDetails>
            <CreateGame />
          </RequirePlayerDetails>
        ),
      },
      {
        path: "/play/join",
        element: (
          <RequirePlayerDetails>
            <EnterGameCode />
          </RequirePlayerDetails>
        ),
      },
      {
        path: "/game/:roomCode",
        element: <GameRoute />,
      },
      { path: "/howtoplay", element: <Help about={false} /> },
      { path: "/about", element: <Help about={true} /> },
      { path: "/contact", element: <Contact /> },
      { path: "/bugreport", element: <BugReport /> },
      { path: "/terms", element: <Terms /> },
      { path: "/privacy", element: <Privacy /> },
      { path: "/codeofconduct", element: <CodeOfConduct /> },
      { path: "/blog", element: <Blog /> },
      { path: "/supportus", element: <SupportUs /> },
      { path: "/supporter", element: <Supporter /> },
      { path: "/thankyou", element: <Thankyou /> },
      {
        path: "/zine",
        element: (
          <RequirePremium>
            <Zines />
          </RequirePremium>
        ),
      },
      { path: "/tobeyraimi", element: <Admin /> },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
];

